/**
 * jaiio-facets-ui — mécanique d'interface partagée par les deux barres de facettes :
 * <facet-bar> (filtres natifs Shopify) et <facet-bar-engine> (moteur maison).
 *
 * Ce que ce module porte :
 *   - l'ancrage des panneaux (toujours sous la pill, ancrage droit, voile,
 *     suivi de la pill au défilement) ;
 *   - l'accessibilité clavier : piège de focus, retour au déclencheur ;
 *   - l'ouverture et la fermeture des panneaux, de la feuille et du drill-down ;
 *   - les tranches de prix (paliers, enveloppe, état coché) ;
 *   - « Plus de filtres » et l'entrée « Sauvegarder cette recherche » ;
 *   - « Ma taille » : bloc profil, ligne d'enregistrement, appel `my-sizes`.
 *
 * Les deux barres gardent leur état (`_openPanelId`, `_openSheetEl`,
 * `_drilldownId`…) et délèguent le geste : ce qui diffère vraiment entre elles
 * — d'où viennent les valeurs, comment se fait le rendu — reste chez elles.
 */
(function () {
  "use strict";

  // Le panneau prolonge la barre : il pend sur son filet du bas, sans espace
  // (maquette `.fpan`, `top: 100%`). EDGE est la marge gardée aux bords du viewport.
  var GAP = 0;
  var EDGE = 12;
  // En dessous de cette hauteur, on n'essaie plus de faire tenir le panneau : il déborde.
  var MIN_PANEL_HEIGHT = 220;

  // Coche des cases (listes, tranches de prix, arbre) : une seule source pour
  // les deux barres, qui ne peuvent pas rendre le snippet `icon` du thème.
  var CHECK_ICON = '<svg viewBox="0 0 24 24" width="12" height="12"><path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(",");

  // Un élément masqué (hidden, display:none, filtré par la recherche de facette)
  // n'a ni boîte ni rectangle : il sort du parcours de tabulation.
  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isVisible);
  }

  /**
   * Ancre `panel` sous `trigger`, toujours : un panneau qui s'ouvrait au-dessus
   * de la pill quand la place manquait en bas désorientait (retour d'Antoine du
   * 2026-09-24). La hauteur est plafonnée à la place restante sous la pill — le
   * corps du panneau (flex, min-height:0) prend le défilement — sans descendre
   * sous MIN_PANEL_HEIGHT.
   */
  function positionPanel(panel, trigger) {
    var rect = trigger.getBoundingClientRect();
    // Lever le plafond de hauteur pour mesurer le panneau remet ses listes en
    // haut : on garde leur défilement, le recalage au scroll de page repasse ici.
    var scrollers = Array.prototype.slice.call(panel.querySelectorAll(".facet-panel__body, [data-scrollable]"));
    var scrollTops = scrollers.map(function (el) { return el.scrollTop; });
    panel.style.position = "fixed";
    panel.style.maxHeight = "";
    panel.style.left = "0px";

    var room = Math.max(window.innerHeight - rect.bottom - GAP - EDGE, MIN_PANEL_HEIGHT);
    if (panel.offsetHeight > room) panel.style.maxHeight = room + "px";
    panel.style.top = Math.round(rect.bottom + GAP) + "px";

    // Aligné à gauche de la pill ; s'il déborderait, le panneau s'ancre sur le
    // bord DROIT de sa pill — c'est le `.right` de la maquette pour les dernières
    // facettes, obtenu ici sans désigner de facette en particulier.
    var width = panel.offsetWidth;
    var left = rect.left;
    if (left + width > window.innerWidth - EDGE) left = rect.right - width;
    left = Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE));
    panel.style.left = Math.round(left) + "px";
    scrollers.forEach(function (el, i) { if (el.scrollTop !== scrollTops[i]) el.scrollTop = scrollTops[i]; });
  }

  /**
   * Un panneau ouvert suit sa pill quand la page défile (retour du 2026-10-07 :
   * le premier défilement le fermait, on perdait sa place dans les filtres). Le
   * panneau est posé en `fixed` depuis la pill, et la pill bouge : la barre
   * n'est collante qu'une fois atteinte, puis l'en-tête se condense en 0,32 s.
   * On recale donc à chaque image pendant le défilement et encore un peu
   * après, le temps que l'en-tête finisse sa transition.
   */
  var FOLLOW_SETTLE_MS = 400;

  function followPanel(bar) {
    if (!bar._openPanelId || isMobile()) return;
    bar._followUntil = performance.now() + FOLLOW_SETTLE_MS;
    if (bar._followRaf) return;
    var step = function () {
      bar._followRaf = null;
      var panel = bar._openPanelId ? panelOf(bar, bar._openPanelId) : null;
      var trigger = bar._openTrigger;
      if (!panel || !trigger || !trigger.isConnected) return;
      positionPanel(panel, trigger);
      setScrim(bar, true);
      if (performance.now() < bar._followUntil) bar._followRaf = requestAnimationFrame(step);
    };
    bar._followRaf = requestAnimationFrame(step);
  }

  function stopFollowing(bar) {
    if (!bar._followRaf) return;
    cancelAnimationFrame(bar._followRaf);
    bar._followRaf = null;
  }

  /**
   * Voile des résultats pendant qu'un panneau desktop est ouvert : il part sous
   * la rangée des pills, dont seule la mesure du moment donne la position (barre
   * collante, en-tête qui se condense au scroll). Sous la rangée et non sous la
   * barre : les filtres actifs, rendus dans la barre, sont voilés avec la grille
   * (retour d'Antoine du 2026-09-24).
   */
  function setScrim(bar, open) {
    var scrim = bar.querySelector("[data-scrim]");
    if (!scrim) return;
    if (!open) {
      scrim.classList.remove("is-open");
      scrim.hidden = true;
      return;
    }
    var edge = bar.querySelector(".facet-bar__pills") || bar;
    scrim.style.top = Math.round(edge.getBoundingClientRect().bottom) + "px";
    // Déjà visible (recalage au défilement) : seul le bord haut change.
    if (!scrim.hidden) return;
    scrim.hidden = false;
    requestAnimationFrame(function () { scrim.classList.add("is-open"); });
  }

  /** Remet le panneau dans son état non positionné (appelé à la fermeture). */
  function clearPosition(panel) {
    panel.style.position = "";
    panel.style.top = "";
    panel.style.left = "";
    panel.style.maxHeight = "";
  }

  /**
   * Enferme la tabulation dans `container`. L'écouteur vit sur le conteneur :
   * il ne se déclenche que si le focus est dedans, donc deux pièges imbriqués
   * (feuille + drill-down) coexistent sans se marcher dessus.
   * Renvoie la fonction de libération.
   */
  function trapFocus(container) {
    function onKeydown(event) {
      if (event.key !== "Tab") return;
      var items = focusables(container);
      if (!items.length) return event.preventDefault();
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
    container.addEventListener("keydown", onKeydown);
    return function () {
      container.removeEventListener("keydown", onKeydown);
    };
  }

  /**
   * Donne le focus au premier élément focusable du conteneur. `mode: "container"`
   * vise le conteneur lui-même : utilisé après un re-rendu AJAX, où sauter sur un
   * élément déplacerait le défilement que l'on vient de restaurer.
   */
  function focusFirst(container, mode) {
    var target = null;
    if (mode !== "container") target = focusables(container)[0] || null;
    if (!target) {
      container.setAttribute("tabindex", "-1");
      target = container;
    }
    target.focus({ preventScroll: true });
  }

  /* ─────────────────────────── Tranches de prix ───────────────────────────
     Les deux moteurs affichent les mêmes tranches, cochables à plusieurs.
     Le filtre de prix ne porte qu'une plage (`prix_min` / `prix_max`) : deux
     tranches cochées donnent donc la plage qui les contient toutes les deux. */

  // Paliers canoniques, rabotés au prix le plus haut réellement affiché
  // (Q38 : borne haute dynamique, jamais un plafond figé). Un catalogue
  // resserré reçoit des paliers arrondis plutôt que des tranches vides.
  var PRICE_LADDER = [50, 100, 200, 500];

  function priceTiers(rangeMax) {
    var tiers = PRICE_LADDER.filter(function (v) { return v < rangeMax; });
    if (tiers.length) return tiers;
    var step = Math.max(5, Math.round(rangeMax / 3 / 5) * 5);
    return [step, step * 2].filter(function (v) { return v < rangeMax; });
  }

  /** Tranches [{min, max}] ; la dernière est ouverte (max null). */
  function priceRanges(rangeMax) {
    var tiers = priceTiers(rangeMax);
    var out = tiers.map(function (tier, i) {
      return { min: i === 0 ? 0 : tiers[i - 1], max: tier };
    });
    if (tiers.length) out.push({ min: tiers[tiers.length - 1], max: null });
    return out;
  }

  /** Plage qui contient toutes les tranches cochées. */
  function priceHull(ranges, picked) {
    var mins = [];
    var maxes = [];
    picked.forEach(function (i) {
      if (!ranges[i]) return;
      mins.push(ranges[i].min);
      maxes.push(ranges[i].max);
    });
    if (!mins.length) return null;
    return {
      min: Math.min.apply(null, mins),
      max: maxes.indexOf(null) !== -1 ? null : Math.max.apply(null, maxes)
    };
  }

  /**
   * Tranches à montrer cochées pour la plage courante : celles qu'elle contient,
   * à condition que leur enveloppe fasse exactement cette plage. Sinon la jauge
   * a été bougée à la main, et aucune tranche ne la décrit.
   */
  function activePriceRanges(ranges, min, max) {
    if (min == null && max == null) return [];
    var lo = min == null ? 0 : min;
    var hi = max == null ? null : max;
    var picked = [];
    ranges.forEach(function (r, i) {
      var contained = r.min >= lo && (hi == null || (r.max != null && r.max <= hi));
      if (contained) picked.push(i);
    });
    var hull = priceHull(ranges, picked);
    if (!hull) return [];
    var sameMax = hull.max == null ? hi == null : hull.max === hi;
    return hull.min === lo && sameMax ? picked : [];
  }

  function config() {
    return window.jaiioSearchConfig || {};
  }
  function facetText() {
    var c = config();
    return (c.text && c.text.facets) || {};
  }
  // Les jetons sont encadrés de pourcents : une accolade simple dans un `{{ … }}`
  // ferait rejeter le fichier Liquid qui porte ces chaînes (voir TESTS § 9.4).
  function fill(template, values) {
    return String(template || "").replace(/%([a-z_]+)%/g, function (m, k) {
      return values[k] != null ? values[k] : m;
    });
  }
  function esc(value) {
    return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function priceLabel(range, text, currency) {
    var money = function (v) { return v + " " + currency; };
    if (range.max == null) return fill(text.price_over, { amount: money(range.min) });
    if (range.min === 0) return fill(text.price_under, { amount: money(range.max) });
    return fill(text.price_between, { from: String(range.min), to: money(range.max) });
  }

  /** Markup des tranches, état coché compris. */
  function pricePresetsHtml(rangeMax, min, max) {
    var text = facetText();
    var currency = config().currency_symbol || "€";
    var ranges = priceRanges(rangeMax);
    var active = activePriceRanges(ranges, min, max);
    return ranges.map(function (r, i) {
      var on = active.indexOf(i) !== -1;
      // L'état coché s'écrit dans `aria-pressed`, et le CSS s'y accroche : pas de
      // classe parallèle à tenir en phase.
      // Case à cocher dessinée comme celles des listes (`.facet-check__box`).
      return '<button type="button" class="facet-price__preset" aria-pressed="' + on +
        '" data-price-min="' + r.min +
        '" data-price-max="' + (r.max == null ? "" : r.max) + '">' +
        '<span class="facet-check__box" aria-hidden="true">' + CHECK_ICON + "</span>" +
        esc(priceLabel(r, text, currency)) + "</button>";
    }).join("");
  }

  /**
   * Plage obtenue en basculant une tranche, les autres cochées étant conservées.
   * Renvoie null quand plus rien n'est coché (le filtre de prix disparaît).
   */
  function togglePricePreset(container, button, rangeMax) {
    var ranges = priceRanges(rangeMax);
    var buttons = Array.prototype.slice.call(container.querySelectorAll(".facet-price__preset"));
    var picked = [];
    buttons.forEach(function (b, i) {
      var on = b.getAttribute("aria-pressed") === "true";
      if (b === button) on = !on;
      if (on) picked.push(i);
    });
    var hull = priceHull(ranges, picked);
    buttons.forEach(function (b, i) {
      var on = picked.indexOf(i) !== -1;
      b.setAttribute("aria-pressed", String(on));
    });
    return hull;
  }

  /* ───────────────────── Sauvegarder cette recherche ─────────────────────
     La mécanique (popovers, endpoint, doublons, fréquence) vit dans
     `assets/save-search.js`, rendue par `snippets/save-search.liquid` ; la barre
     de facettes ne fait que la déclencher, en passant le bouton pour que le
     popover s'y ancre. */

  function saveSearchAvailable() {
    return !!document.querySelector("[data-save-search]");
  }

  /** Déclenche l'enregistrement ; false si la page ne porte pas la mécanique. */
  function triggerSaveSearch(trigger) {
    if (!window.jaiioSaveSearch) return false;
    window.jaiioSaveSearch.open(trigger);
    return true;
  }

  /**
   * Une page sans mécanique d'enregistrement ne doit offrir aucune entrée « alerte ».
   * La marque est posée sur `<html>`, pas sur les boutons : le rendu des barres
   * et de la grille les recrée sans cesse, une classe de racine, elle, survit.
   * Le CSS de `facet-bar.css` fait le reste.
   */
  function markSaveSearchAvailability() {
    document.documentElement.classList.toggle("no-save-search", !saveSearchAvailable());
  }

  // Entrées « Créer une alerte » hors de la barre — le zéro résultat, par exemple.
  // Un clic délégué suffit : pas de composant de plus pour un bouton qui ne fait
  // que réveiller la mécanique commune.
  document.addEventListener("click", function (event) {
    var el = event.target.closest && event.target.closest("[data-jaiio-save-search]");
    if (!el) return;
    event.preventDefault();
    triggerSaveSearch(el);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", markSaveSearchAvailability);
  } else {
    markSaveSearchAvailability();
  }


  /* ═══════════════════ « Ma taille » ═══════════════════
     Maquette `collections.dc.html` (`.mablk`, `.masave`) : on coche ses tailles
     directement, sans passer par la connexion. Le bloc de tête (bascule, rappel,
     « Mettre à jour mes tailles ») n'existe qu'avec des tailles enregistrées ;
     sans elles, la ligne du pied propose de les enregistrer (cliente) ou de créer
     un compte (visiteuse). Les deux barres passent leur sélection
     `{ taille: [], pointure: [] }` ; ce module décide de l'affichage et parle à
     `/apps/jaiio-api/my-sizes` (métachamps `custom.tailles` / `custom.pointures`). */

  var MY_SIZES_URL = "/apps/jaiio-api/my-sizes";
  // Tailles d'une visiteuse partie créer son compte : enregistrées au retour de
  // la connexion. Au-delà du délai, une connexion sans rapport ne doit plus les reprendre.
  var PENDING_SIZES_KEY = "jaiio:pending-sizes";
  var PENDING_SIZES_TTL = 60 * 60 * 1000;

  function sizeList(value) {
    return Array.isArray(value) ? value.map(String) : [];
  }
  function parseSizes(raw) {
    try { return sizeList(JSON.parse(raw || "[]")); } catch (e) { return []; }
  }
  // Le profil et les valeurs du moteur viennent du même référentiel, mais la casse
  // n'est garantie nulle part : on compare en minuscules.
  function lowerAll(list) {
    return list.map(function (s) { return String(s).toLowerCase(); });
  }
  function sameSizes(a, b) {
    var lb = lowerAll(b);
    return a.length === b.length && lowerAll(a).every(function (s) { return lb.indexOf(s) !== -1; });
  }
  function hasSomeOf(list, selected) {
    var ls = lowerAll(selected);
    return lowerAll(list).some(function (s) { return ls.indexOf(s) !== -1; });
  }
  // « M, L · 38 » : tailles puis pointures (maquette).
  function sizesText(sizes) {
    return [sizes.taille.join(", "), sizes.pointure.join(", ")].filter(Boolean).join(" · ");
  }

  function sizesBlock(bar) {
    return bar.querySelector("[data-mataille-block]");
  }
  function sizesPanel(block) {
    return block.closest("[data-panel]") || block.parentNode;
  }
  function savedSizes(block) {
    return {
      taille: parseSizes(block.getAttribute("data-my-tailles")),
      pointure: parseSizes(block.getAttribute("data-my-pointures"))
    };
  }

  /**
   * Groupes que la mise à jour remplace : ceux que la page propose (la barre
   * expose `_sizesOffered()`) ou qui ont une coche. Décocher toutes ses tailles
   * en gardant ses pointures retire donc les tailles du profil ; une collection
   * sans chaussures, elle, ne touche pas aux pointures enregistrées.
   */
  function offeredSizes(bar, selected) {
    var offered = bar._sizesOffered ? bar._sizesOffered() : {};
    return {
      taille: !!offered.taille || selected.taille.length > 0,
      pointure: !!offered.pointure || selected.pointure.length > 0
    };
  }

  // Profil après enregistrement : la sélection pour les groupes concernés, le
  // profil actuel pour les autres.
  function nextSizes(saved, selected, offered) {
    return {
      taille: offered.taille ? selected.taille.slice() : saved.taille,
      pointure: offered.pointure ? selected.pointure.slice() : saved.pointure
    };
  }

  /** Met le bloc et la ligne du pied d'accord avec la sélection et le profil. */
  function renderMySizes(bar, selected) {
    var block = sizesBlock(bar);
    if (!block) return;
    var panel = sizesPanel(block);
    var saved = savedSizes(block);
    var hasSaved = saved.taille.length + saved.pointure.length > 0;
    var chosen = selected.taille.length + selected.pointure.length > 0;
    // Rien de coché, c'est « je ne filtre pas sur mes tailles », pas « j'efface
    // mes tailles » : aucun bouton, ni « à jour » ni « mettre à jour ».
    var next = nextSizes(saved, selected, offeredSizes(bar, selected));
    var dirty = chosen && !(sameSizes(next.taille, saved.taille) && sameSizes(next.pointure, saved.pointure));

    block.hidden = !hasSaved;
    if (hasSaved) {
      var on = hasSomeOf(saved.taille, selected.taille) || hasSomeOf(saved.pointure, selected.pointure);
      block.querySelector('[data-action="mataille-toggle"]').setAttribute("aria-checked", String(on));
      block.querySelector("[data-mataille-recap]").textContent = sizesText(saved);
      var update = block.querySelector("[data-mataille-update]");
      update.hidden = !chosen;
      update.disabled = !dirty || !!bar._mySizesBusy;
      update.textContent = update.getAttribute(dirty ? "data-label-update" : "data-label-done");
    }

    var hint = panel.querySelector("[data-mataille-hint]");
    if (hint) hint.hidden = hasSaved || chosen;
    var row = panel.querySelector("[data-mataille-save]");
    if (row) {
      row.hidden = hasSaved || !chosen;
      row.querySelector("[data-mataille-save-text]").textContent =
        row.getAttribute("data-template").replace("§", sizesText(selected));
    }
    // Un message « enregistrées » ne doit pas survivre à une sélection qui s'en écarte.
    var status = panel.querySelector("[data-mataille-status]");
    if (status && dirty && !bar._mySizesBusy) status.textContent = "";
  }

  /**
   * Bascule « Ma taille » : coche les tailles enregistrées parmi celles que la
   * page propose (`available`), ou les décoche ; les tailles cochées à la main
   * restent. Rend la nouvelle sélection, que la barre applique.
   */
  function toggledSizes(bar, selected, available) {
    var block = sizesBlock(bar);
    var saved = savedSizes(block);
    var on = block.querySelector('[data-action="mataille-toggle"]').getAttribute("aria-checked") !== "true";
    function group(key) {
      var mine = lowerAll(saved[key]);
      var isMine = function (v) { return mine.indexOf(String(v).toLowerCase()) !== -1; };
      var kept = selected[key].filter(function (v) { return !isMine(v); });
      return on ? kept.concat(available[key].filter(isMine)) : kept;
    }
    return { taille: group("taille"), pointure: group("pointure") };
  }

  function setSizesBusy(panel, busy) {
    panel.querySelectorAll('[data-action="mataille-save"]').forEach(function (btn) {
      btn.disabled = busy;
    });
  }

  /**
   * Enregistre la sélection comme tailles de la cliente, panneau ouvert.
   * `getSelected` relit la sélection au retour : elle a pu changer pendant l'appel.
   * `offered` : groupes remplacés (par défaut ceux de la page, voir offeredSizes).
   */
  function saveMySizes(bar, selected, getSelected, offered) {
    var block = sizesBlock(bar);
    if (!block || bar._mySizesBusy) return;
    var panel = sizesPanel(block);
    var status = panel.querySelector("[data-mataille-status]");
    var next = nextSizes(savedSizes(block), selected, offered || offeredSizes(bar, selected));
    var hadFocus = panel.contains(document.activeElement);
    var message = "error";

    bar._mySizesBusy = true;
    setSizesBusy(panel, true);
    if (status) status.textContent = "";

    fetch(MY_SIZES_URL, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ tailles: next.taille, pointures: next.pointure })
    }).then(function (res) {
      if (!res.ok) throw new Error("my-sizes POST " + res.status);
      block.setAttribute("data-my-tailles", JSON.stringify(next.taille));
      block.setAttribute("data-my-pointures", JSON.stringify(next.pointure));
      message = "saved";
    }).catch(function (e) {
      console.error("[facets][my-sizes]", e);
    }).then(function () {
      bar._mySizesBusy = false;
      setSizesBusy(panel, false);
      renderMySizes(bar, getSelected());
      if (status) status.textContent = status.getAttribute("data-" + message);
      // Le bouton cliqué a pu disparaître (ligne du pied remplacée par le bloc) et
      // le navigateur rend alors le focus à <body> : il revient dans le panneau.
      var active = document.activeElement;
      if (hadFocus && (!panel.contains(active) || !isVisible(active))) {
        if (block.hidden) focusFirst(panel);
        else block.querySelector('[data-action="mataille-toggle"]').focus({ preventScroll: true });
      }
    });
  }

  /** Visiteuse : garde la sélection pour le retour, puis part se connecter. */
  function signupWithSizes(button, selected) {
    try {
      localStorage.setItem(PENDING_SIZES_KEY, JSON.stringify({
        taille: selected.taille, pointure: selected.pointure, at: Date.now()
      }));
    } catch (e) { /* stockage refusé : les filtres restent dans l'adresse de retour */ }
    var back = window.location.pathname + window.location.search;
    window.location.href = button.getAttribute("data-login-url") + "?return_to=" + encodeURIComponent(back);
  }

  /** Au retour de la connexion : enregistre les tailles choisies avant de partir. */
  function resumePendingSizes(bar, getSelected) {
    var block = sizesBlock(bar);
    if (!block || !block.hasAttribute("data-logged-in")) return;
    var pending = null;
    try {
      pending = JSON.parse(localStorage.getItem(PENDING_SIZES_KEY) || "null");
      localStorage.removeItem(PENDING_SIZES_KEY);
    } catch (e) {
      return;
    }
    if (!pending || !(Date.now() - pending.at < PENDING_SIZES_TTL)) return;
    var sizes = { taille: sizeList(pending.taille), pointure: sizeList(pending.pointure) };
    if (sizes.taille.length + sizes.pointure.length === 0) return;
    // Seuls les groupes cochés par la visiteuse : un compte existant garde le reste.
    saveMySizes(bar, sizes, getSelected, { taille: sizes.taille.length > 0, pointure: sizes.pointure.length > 0 });
  }


  /* ═══════════════════ Mécanique des panneaux et de la feuille ═══════════════════
     Les deux barres ouvrent, ferment et positionnent leurs panneaux de la même
     façon : la mécanique vit ici, l'état reste sur l'élément (`bar._openPanelId`,
     `bar._openSheetEl`, `bar._drilldownId`…) pour que la sauvegarde et la
     restauration d'état de chaque moteur continuent de le lire.

     Un moteur qui a besoin d'un geste propre à l'ouverture (charger l'arbre des
     catégories, par exemple) expose `_onPanelOpen(panelId)`. */

  // Au-dessous, c'est la feuille mobile qui prend la main, pas le popover.
  var DESKTOP_MQ = "(min-width: 1000px)";

  function isMobile() {
    return !window.matchMedia(DESKTOP_MQ).matches;
  }

  function panelOf(bar, panelId) {
    return bar.querySelector("#" + CSS.escape(panelId));
  }

  function togglePanel(bar, trigger) {
    var panelId = trigger.getAttribute("aria-controls");
    // En mobile, une pill ouvre un écran plein, pas un popover.
    if (isMobile()) return openDrilldown(bar, panelId);
    if (bar._openPanelId === panelId) return closePanel(bar);
    openPanel(bar, panelId, trigger);
  }

  // focusMode "container" (après un re-rendu) pose le focus sur le panneau
  // lui-même : sauter sur une option déplacerait le défilement qu'on vient de
  // restaurer.
  function openPanel(bar, panelId, trigger, focusMode) {
    closePanel(bar);
    var panel = panelOf(bar, panelId);
    if (!panel) return;

    bar._openPanelId = panelId;
    bar._openTrigger = trigger;
    panel.hidden = false;
    panel.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    var wrap = trigger.closest(".facet-pill-wrap");
    if (wrap) wrap.classList.add("is-open");

    if (bar._onPanelOpen) bar._onPanelOpen(panelId);
    positionPanel(panel, trigger);
    setScrim(bar, true);
    bar._panelTrap = trapFocus(panel);
    focusFirst(panel, focusMode);
  }

  function closePanel(bar) {
    if (!bar._openPanelId) return;
    var panel = panelOf(bar, bar._openPanelId);
    // Le focus ne retourne à la pill que s'il était dans le panneau : un clic
    // extérieur ne doit pas le voler à l'élément que la visiteuse vient de viser.
    var hadFocus = !!panel && panel.contains(document.activeElement);
    stopFollowing(bar);
    if (bar._panelTrap) {
      bar._panelTrap();
      bar._panelTrap = null;
    }
    if (panel) {
      panel.classList.remove("is-open");
      panel.hidden = true;
      clearPosition(panel);
    }
    setScrim(bar, false);
    if (bar._openTrigger) {
      bar._openTrigger.setAttribute("aria-expanded", "false");
      var wrap = bar._openTrigger.closest(".facet-pill-wrap");
      if (wrap) wrap.classList.remove("is-open");
      if (hadFocus) bar._openTrigger.focus({ preventScroll: true });
    }
    bar._openPanelId = null;
    bar._openTrigger = null;
  }

  // La pill qui ouvre une feuille est retrouvée par sélecteur et non gardée en
  // référence : un re-rendu remplace le DOM de la barre entre l'ouverture et la
  // fermeture, et le focus doit revenir sur la pill vivante.
  function sheetTrigger(bar, targetSel) {
    return targetSel
      ? bar.querySelector('[data-action="open-sheet"][data-sheet-target="' + targetSel + '"]')
      : bar.querySelector('[data-action="open-sheet"]:not([data-sheet-target])');
  }

  function openSheet(bar, targetSel) {
    var sheet = targetSel ? bar.querySelector(targetSel) : bar.querySelector("[data-sheet]");
    if (!sheet) return;
    bar._openSheetEl = sheet;
    bar._openSheetTarget = targetSel || null;
    sheet.hidden = false;
    sheet.setAttribute("aria-hidden", "false");
    requestAnimationFrame(function () { sheet.classList.add("is-open"); });
    document.documentElement.classList.add("facet-sheet-open");
    trapSheet(bar, sheet);
  }

  function trapSheet(bar, sheet, focusMode) {
    var dialog = sheet.querySelector(".facet-sheet__panel") || sheet;
    bar._sheetTrap = trapFocus(dialog);
    focusFirst(dialog, focusMode);
  }

  function closeSheet(bar) {
    var sheet = bar._openSheetEl;
    if (!sheet) return;
    closeDrilldown(bar);
    var hadFocus = sheet.contains(document.activeElement);
    var trigger = sheetTrigger(bar, bar._openSheetTarget);
    if (bar._sheetTrap) {
      bar._sheetTrap();
      bar._sheetTrap = null;
    }
    bar._openSheetEl = null;
    bar._openSheetTarget = null;
    sheet.classList.remove("is-open");
    sheet.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("facet-sheet-open");
    if (hadFocus && trigger) trigger.focus({ preventScroll: true });
    var onEnd = function () {
      if (!bar._openSheetEl) sheet.hidden = true;
      sheet.removeEventListener("transitionend", onEnd);
    };
    sheet.addEventListener("transitionend", onEnd);
    setTimeout(function () { if (!bar._openSheetEl) sheet.hidden = true; }, 400);
  }

  function openDrilldown(bar, panelId, focusMode) {
    var panel = panelOf(bar, panelId);
    if (!panel) return;
    bar._drilldownId = panelId;
    panel.hidden = false;
    panel.classList.add("is-drilldown");
    requestAnimationFrame(function () { panel.classList.add("is-open"); });
    if (bar._onPanelOpen) bar._onPanelOpen(panelId);
    bar._drillTrap = trapFocus(panel);
    focusFirst(panel, focusMode);
  }

  function closeDrilldown(bar) {
    if (!bar._drilldownId) return;
    var panel = panelOf(bar, bar._drilldownId);
    // Le drill-down vient d'une ligne de la feuille ; à défaut (tri ouvert
    // directement depuis une pill), le focus retourne à la pill.
    var back =
      bar.querySelector('[data-action="open-drilldown"][aria-controls="' + bar._drilldownId + '"]') ||
      bar.querySelector('[data-action="toggle-panel"][aria-controls="' + bar._drilldownId + '"]');
    var hadFocus = !!panel && panel.contains(document.activeElement);
    if (bar._drillTrap) {
      bar._drillTrap();
      bar._drillTrap = null;
    }
    bar._drilldownId = null;
    if (hadFocus && back) back.focus({ preventScroll: true });
    if (!panel) return;
    panel.classList.remove("is-open");
    var finish = function () {
      if (panel.classList.contains("is-open")) return; // ré-ouvert entre-temps
      panel.classList.remove("is-drilldown");
      panel.hidden = true;
    };
    panel.addEventListener("transitionend", finish, { once: true });
    setTimeout(finish, 350);
  }

  /** Escape ferme la couche la plus haute. Renvoie true si quelque chose s'est fermé. */
  function closeTopLayer(bar) {
    if (bar._drilldownId) { closeDrilldown(bar); return true; }
    if (bar._openSheetEl) { closeSheet(bar); return true; }
    if (bar._openPanelId) { closePanel(bar); return true; }
    return false;
  }

  /** « Plus de filtres » : déplie les pills secondaires et met le bouton d'accord. */
  function toggleMore(bar, btn) {
    var scroll = bar.querySelector("[data-pills-scroll]");
    if (!scroll) return false;
    var expanded = scroll.classList.toggle("is-more-expanded");
    syncMoreToggle(bar, expanded, btn);
    return expanded;
  }

  function syncMoreToggle(bar, expanded, btn) {
    var toggle = btn || bar.querySelector('[data-action="toggle-more"]');
    if (!toggle) return;
    toggle.setAttribute("aria-expanded", String(expanded));
    var label = toggle.querySelector("[data-more-label]");
    var text = facetText();
    if (label) label.textContent = expanded ? text.less_filters : text.more_filters;
  }

  /**
   * Compteur de l'en-tête de collection (`[data-jaiio-count]`, rendu par
   * `sections/collection-banner.liquid`). Le serveur écrit le nombre de pièces de
   * la collection ; seul le moteur connaît le total réellement affiché — les
   * indisponibles sont masqués — et le total sous filtres.
   */
  function updateCollectionCount(total, filtered) {
    var el = document.querySelector("[data-jaiio-count]");
    if (!el || total == null) return;
    var text = facetText();
    var form = filtered ? text.pieces_count_filtered : text.pieces_count;
    if (!form) return;
    el.textContent = fill(total > 1 ? form.other : form.one, { count: total });
  }

  window.JaiioFacetsUI = {
    isMobile: isMobile,
    togglePanel: togglePanel,
    openPanel: openPanel,
    closePanel: closePanel,
    openSheet: openSheet,
    closeSheet: closeSheet,
    trapSheet: trapSheet,
    openDrilldown: openDrilldown,
    closeDrilldown: closeDrilldown,
    closeTopLayer: closeTopLayer,
    toggleMore: toggleMore,
    CHECK_ICON: CHECK_ICON,
    syncMoreToggle: syncMoreToggle,
    positionPanel: positionPanel,
    followPanel: followPanel,
    setScrim: setScrim,
    updateCollectionCount: updateCollectionCount,
    clearPosition: clearPosition,
    trapFocus: trapFocus,
    focusFirst: focusFirst,
    focusables: focusables,
    priceRanges: priceRanges,
    activePriceRanges: activePriceRanges,
    pricePresetsHtml: pricePresetsHtml,
    togglePricePreset: togglePricePreset,
    triggerSaveSearch: triggerSaveSearch,
    markSaveSearchAvailability: markSaveSearchAvailability,
    parseSizes: parseSizes,
    renderMySizes: renderMySizes,
    toggledSizes: toggledSizes,
    saveMySizes: saveMySizes,
    signupWithSizes: signupWithSizes,
    resumePendingSizes: resumePendingSizes
  };
})();
